import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import constants from 'helpers/constants';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import Icon from 'components/atoms/Icons/Icon/Icon';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import Button from 'components/atoms/Buttons/Button/Button';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import {Span} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import Close from '@mui/icons-material/Close';
import Save from '@mui/icons-material/Save';
import StyledEntityEnrichDialog from 'components/organisms/Dialogs/EntityEnrichDialog/EntityEnrichDialog.styles';
import {processEntityFeedback, useEntityCsi} from 'services/entity/entity.utils';
import {useAuthIsVentureIq} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import FeedbackHelper from 'components/molecules/Helpers/FeedbackHelper/FeedbackHelper';

const EntityEnrichDialog = React.forwardRef((props, ref) => {
  const {
    title,
    entity,
    fields,
    fieldData,
    onClose,
    onChange,
    onSubmit,
    ...innerProps
  } = useComponentProps(props, 'EntityEnrichDialog');

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(null);
  const [context, setContext] = useState(null);
  const [validation, setValidation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useImperativeHandle(ref, () => innerRef.current);

  const csi = useEntityCsi(entity);
  const isVentureIq = useAuthIsVentureIq();

  const fieldsMemo = useMemo(() => {
    return fields.reduce((a, field) => {
      field = utils.initializeFormFields([field], {
        ...csi,
        ...entity,
        tags: isVentureIq ? entity.systemTags : entity.tags,
      })[0];

      const feedback = utils.isFunction(field.feedback) ? field.feedback(entity, field.initial) : field.feedback;
      const info = utils.isFunction(field.info) ? field.info(entity, field.initial) : field.info;

      field.FormFieldProps = field.FormFieldProps ?? {};

      field = {
        ...field,
        context: {
          ...(utils.isFunction(field.context) ? field.context(entity, field.initial) : field.context),
          ...context
        },
        labelPostfix: field.labelPostfix ?? info,
        FormFieldProps: {
          ...field.FormFieldProps,
          variant: 'staticLabel',
          ...(field.link ? {hiddenAdornmentLabel: true} : null)
        }
      };

      const helperText = field.FormFieldProps?.helperText ?? '';
      field.FormFieldProps = {
        ...field.FormFieldProps,
        helperText: feedback?.show ?
          ((field, value, disabled) => {
            if (!disabled) {
              const feedback = processEntityFeedback(entity, field, value?.value ?? value);
              return <FeedbackHelper feedback={feedback}
                                     fallback={helperText}
                                     acceptField={!feedback?.suggestion ? `${utils.camelcase(field.name)}Verified` : null}
                                     ignoreField={`${utils.camelcase(field.name)}Verified`}
                                     suggestionField={field.name}
                                     variant="compact"/>
            }
          }) : helperText
      };

      a.push(field);

      if (feedback) {
        a.push({
          name: `${utils.camelcase(field.name)}Verified`,
          type: constants.formFieldTypes.text,
          parent: field,
          initial: feedback.verified,
          FormFieldProps: {
            hidden: true
          }
        });
      }

      return a;
    }, []);
  }, [csi, entity, fields, isVentureIq, context]);

  const handleChange = (field, value) => {
    onChange?.(field, value);
  };

  const handleChangeDirect = (e) => {
    const field = fieldsMemo?.find((field) => field.name === e?.target?.name);
    const value = e?.target?.value;

    if (['name', 'legalName', 'location', 'city', 'address', 'cocNumber', 'website'].includes(utils.camelcase(field.name))) {
      const contextName = utils.camelcase(field.name) === 'location' ? 'country' : utils.camelcase(field.name);
      const contextValue = utils.camelcase(field.name) === 'location' ? value?.value : value;
      setContext((current) => {
        return {
          ...current,
          [contextName]: contextValue
        }
      })
    }
  };

  const handleSubmit = (values, actions) => {
    setSubmitting(true);

    utils.asPromise(onSubmit)(values, fieldsMemo)
      .then(() => {
        onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        setError('Saving company info failed');
      })
      .finally(() => {
        actions.setSubmitting(false);
        setSubmitting(false);
      });
  };

  const handleValidating = (isValidating, isDirty, hasErrors) => {
    setDirty(isDirty);
    if (hasErrors) {
      setValidation('Please check if all fields have the correct values');
    } else {
      setValidation(null);
    }
  }

  const handleSubmitClick = () => {
    formRef.current?.submit();
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e);
  }

  const handleClose = (e, reason) => {
    if ((!submitting && !error && !dirty) || ['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      onClose?.(e, reason);
    }
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button children={'Cancel'}
              variant="text"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button disabled={submitting}
              type="submit"
              variant="contained"
              children={'Save'}
              startIcon={<Icon icon={Save}/>}
              onClick={handleSubmitClick}/>
    </React.Fragment>
  }

  return <StyledEntityEnrichDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader title={title ?? 'Company info'}/>
    <DialogContent>
      <InlineForm ref={formRef}
                  fields={fieldsMemo}
                  fieldData={fieldData}
                  onValidating={handleValidating}
                  onChange={handleChange}
                  onChangeDirect={handleChangeDirect}
                  onSubmit={handleSubmit} />
    </DialogContent>
    <DialogFooter className="EntityEnrichDialog-footer"
                  info={(error ?? validation) ? <Span color="error">{error ?? validation}</Span> : null}
                  buttons={renderButtons()} />
  </StyledEntityEnrichDialog>
});

EntityEnrichDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  title: PropTypes.string,
  entity: PropTypes.object,
  fields: PropTypes.array,
  fieldData: PropTypes.object,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func
};

export default EntityEnrichDialog;
